import type {
  CreateLink,
  LinkType,
  Page,
  TicketEvent,
  TicketLink,
  Watcher,
} from '@servicetim/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type ItemsResponse, portalPath, useApiContext } from './client';
import { DEFAULT_PAGE_SIZE, pageQuery, usePagedList } from './paging';

const ticketPath = (ticketId: string) => portalPath(`/tickets/${ticketId}`);

/* ----------------------------- watchers ----------------------------- */

/** `GET /v1/tickets/{id}/watchers` */
export function useTicketWatchers(ticketId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.ticket(ticketId ?? '').watchers,
    queryFn: ({ signal }) =>
      request<ItemsResponse<Watcher>>('GET', `${ticketPath(ticketId ?? '')}/watchers`, { signal }),
    select: (r) => r.items,
    enabled: ticketId !== undefined,
  });
}

/** `PUT /v1/tickets/{id}/watchers/{userId}` (add) or `DELETE` (remove). */
export function useSetWatcher(ticketId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ userId, watching }: { userId: string; watching: boolean }) =>
      request<unknown>(watching ? 'PUT' : 'DELETE', `${ticketPath(ticketId)}/watchers/${userId}`, {
        body: watching ? {} : undefined,
      }),
    onSuccess: () =>
      invalidate(
        keys.ticket(ticketId).watchers,
        keys.ticket(ticketId).detail,
        keys.ticket(ticketId).history,
      ),
  });
}

/* ----------------------------- links ----------------------------- */

/** `GET /v1/link-types` (portal; every tenant link type, system ones first). */
export function usePortalLinkTypes() {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: [...keys.linkTypes.all, 'portal'],
    queryFn: ({ signal }) =>
      request<ItemsResponse<LinkType>>('GET', portalPath('/link-types'), { signal }),
    select: (r) => r.items,
    staleTime: 5 * 60_000,
  });
}

/** `GET /v1/tickets/{id}/links` */
export function useTicketLinks(ticketId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.ticket(ticketId ?? '').links,
    queryFn: ({ signal }) =>
      request<ItemsResponse<TicketLink>>('GET', `${ticketPath(ticketId ?? '')}/links`, { signal }),
    select: (r) => r.items,
    enabled: ticketId !== undefined,
  });
}

/** `POST /v1/tickets/{id}/links` */
export function useCreateLink(ticketId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: CreateLink) =>
      request<TicketLink>('POST', `${ticketPath(ticketId)}/links`, { body: input }),
    onSuccess: (link) =>
      invalidate(
        keys.ticket(ticketId).links,
        keys.ticket(ticketId).history,
        keys.ticket(link.ticket.id).links,
        keys.ticket(link.ticket.key).links,
      ),
  });
}

/** `DELETE /v1/tickets/{id}/links/{lid}` */
export function useDeleteLink(ticketId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ linkId }: { linkId: string; otherTicketId?: string }) =>
      request<void>('DELETE', `${ticketPath(ticketId)}/links/${linkId}`),
    onSuccess: (_v, { otherTicketId }) =>
      invalidate(
        keys.ticket(ticketId).links,
        keys.ticket(ticketId).history,
        ...(otherTicketId ? [keys.ticket(otherTicketId).links] : []),
      ),
  });
}

/* ----------------------------- history ----------------------------- */

/** `GET /v1/tickets/{id}/history` (keyset pages). */
export function useTicketHistory(ticketId: string | undefined, pageSize = DEFAULT_PAGE_SIZE) {
  const { request, keys } = useApiContext();
  return usePagedList<TicketEvent>({
    queryKey: [...keys.ticket(ticketId ?? '').history, { pageSize }],
    fetchPage: (cursor, signal) =>
      request<Page<TicketEvent>>('GET', `${ticketPath(ticketId ?? '')}/history`, {
        query: pageQuery(cursor, pageSize),
        signal,
      }),
    enabled: ticketId !== undefined,
  });
}
